import { pushToDataLayer } from './gtm'

/**
 * Known generative-AI referral hosts (TNA-269). Matched against
 * `document.referrer`'s hostname. We deliberately match the *AI* subdomains of
 * Google (gemini/bard) rather than bare `google.com`, because a generic
 * google.com referrer is indistinguishable from ordinary organic search and
 * would misclassify all SEO traffic. True "AI Overviews" clicks are isolated in
 * GA4 via the channel-grouping rule, not here.
 */
const AI_REFERRER_HOSTS: RegExp[] = [
  /(^|\.)chatgpt\.com$/i,
  /(^|\.)chat\.openai\.com$/i,
  /(^|\.)openai\.com$/i,
  /(^|\.)perplexity\.ai$/i,
  /(^|\.)claude\.ai$/i,
  /(^|\.)gemini\.google\.com$/i,
  /(^|\.)bard\.google\.com$/i,
  /(^|\.)copilot\.microsoft\.com$/i,
]

/**
 * Returns the AI referrer hostname if the current page was opened from a known
 * generative-AI platform, else null. Safe to call on the server (returns null).
 */
export function detectAIReferrer(referrer?: string): string | null {
  const ref =
    referrer ?? (typeof document !== 'undefined' ? document.referrer : '')
  if (!ref) return null

  let host: string
  try {
    host = new URL(ref).hostname
  } catch {
    return null
  }

  return AI_REFERRER_HOSTS.some((pattern) => pattern.test(host)) ? host : null
}

/**
 * Detects an AI referral on the entry page and, if found, pushes a dataLayer
 * event so GA4 can group it under a custom "AI Referrals" channel (configured in
 * GA4 Admin against `traffic_source = ai_referral`). Fires at most once per page
 * load. No-op when there's no AI referrer.
 */
export function trackAIReferral(): void {
  const aiHost = detectAIReferrer()
  if (!aiHost) return

  pushToDataLayer({
    event: 'ai_referral',
    traffic_source: 'ai_referral',
    ai_referrer_domain: aiHost,
  })
}
